import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { restrictToParentElement, restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { CSS } from "@dnd-kit/utilities";
import { RxDragHandleDots2 } from "react-icons/rx";

export default function SortableQuestionList({ questions, setQuestions, editable = true }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over) return;
    if (active.id !== over.id) {
      const oldIndex = questions.findIndex((q) => q.id === active.id);
      const newIndex = questions.findIndex((q) => q.id === over.id);
      setQuestions(arrayMove(questions, oldIndex, newIndex));
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      modifiers={[restrictToParentElement, restrictToVerticalAxis]}
    >
      <SortableContext
        items={questions.map((q) => q.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-4 max-h-[45vh] overflow-y-auto scrollbar-hide">
          {questions.map((q) => (
            <SortableItem key={q.id} question={q} editable={editable} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function SortableItem({ question, editable }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: question.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className="flex items-center justify-between p-4 rounded-lg border border-gray-200 bg-gray-100 text-gray-900 dark:text-white"
    >
      <div className="flex items-center gap-2">
        <span className="w-5 h-5">{question.icon}</span>
        <span className="font-medium text-base leading-none">
          {question.select}
          {question.question && (
            <span className="ml-1 text-gray-900 dark:text-gray-300">
              ({question.question || "Untitled"})
            </span>
          )}
        </span>
      </div>
      {editable && (
        <div
          {...listeners}
          className="cursor-grab text-gray-900 hover:text-gray-700 dark:text-gray-300 dark:hover:text-white text-2xl"
        >
          <RxDragHandleDots2 />
        </div>
      )}
    </div>
  );
}
